import api from './api';

/**
 * Data (Menu 4) van de professional: vijf onderdelen, één endpoint per
 * onderdeel. Server: src/routes/professionalData.ts.
 * `null` in een waarde = onderdrukt door de privacy- of ACM-grendel.
 */

const BASIS = '/v1/professional/data';

export const ONDERDELEN = ['beroepsregistratie', 'bedrijfscijfers', 'meldingsmonitor', 'kwaliteit', 'markt'] as const;
export type Onderdeel = (typeof ONDERDELEN)[number];

interface Periode {
  van: string;
  tot: string;
}

// ─── 1 Beroepsregistratie ────────────────────────────────────────────────────

export interface NormMeter {
  behaald: number;
  norm: number;
  restant: number;
  gehaald: boolean;
}

export type CaseRol = 'VTP_ONGEWENST' | 'VTP_INTEGRITEIT' | 'BMW_ARBO' | 'PREVENTIEF';

export interface Beroepsregistratie {
  registratiejaar: number;
  keuze: 'huidig' | 'komend';
  periode: Periode;
  deadline: string;
  pe_punten: NormMeter & { per_register: { register: string; punten: number }[] };
  intervisie: NormMeter & { sessies: number; afgewezen: number };
  praktijk: NormMeter & { facturen: number };
  casussen_per_rol: { rol: CaseRol; label: string; casussen: number }[];
}

export const haalBeroepsregistratie = async (periode: 'huidig' | 'komend'): Promise<Beroepsregistratie> =>
  (await api.get(`${BASIS}/beroepsregistratie`, { params: { periode } })).data.data;

// ─── 2 Bedrijfscijfers ───────────────────────────────────────────────────────

export type RolFilter = 'ALLE' | 'VTP' | 'BMW';
export type RolGroep = 'VTP' | 'BMW' | 'OVERIG';
export type Granulariteit = 'maand' | 'kwartaal' | 'jaar';

export interface FiscaleMatrix {
  netto: number;
  btw: number;
  bruto: number;
  /** Netto onder 21% (de grondslag van laag B). */
  belast_netto: number;
  /** Netto onder 0%: medische vrijstelling (BIG, per rol) en KOR. */
  vrijgesteld: { medisch: number; kor: number };
  arbo_vrijgesteld: { netto: number; btw: number };
  regels: number;
}

export interface Bedrijfscijfers {
  jaar: number;
  granulariteit: Granulariteit;
  deel: number;
  rol: RolFilter;
  periode: Periode;
  matrix: FiscaleMatrix;
  per_rol: (FiscaleMatrix & { rol: RolGroep; label: string })[];
  vergelijking: { periode: Periode; matrix: FiscaleMatrix } | null;
  cashflow: { gerealiseerd: number; pending: number; pending_uitbetaling: number; pending_fiat: number; prognose: number };
  maanden: { maand: string; VTP: number; BMW: number; OVERIG: number; totaal: number }[];
  vorig_jaar: number[];
}

export const haalBedrijfscijfers = async (p: { jaar: number; granulariteit: Granulariteit; deel: number; rol: RolFilter; vergelijk: boolean }): Promise<Bedrijfscijfers> =>
  (await api.get(`${BASIS}/bedrijfscijfers`, { params: { ...p, vergelijk: p.vergelijk ? '1' : '0' } })).data.data;

// ─── 3 Meldingsmonitor ───────────────────────────────────────────────────────

export interface Meldingsmonitor {
  jaar: number;
  kwartaal: number;
  periode_omzet: Periode;
  periode_trajecten: Periode;
  omzet_totaal: number;
  concentratie: { employer_id: string; naam: string; netto: number; pct: number }[];
  opdrachtgevers: number;
  grootste_pct: number | null;
  pools: { employer_id: string; naam: string; actief: number; afgerond: number; gestart: number }[];
}

export const haalMeldingsmonitor = async (jaar: number, kwartaal: number): Promise<Meldingsmonitor> =>
  (await api.get(`${BASIS}/meldingsmonitor`, { params: { jaar, kwartaal } })).data.data;

// ─── 4 Kwaliteitsrapportage ──────────────────────────────────────────────────

export type Dimensie = 'kennis' | 'snelheid' | 'doelgerichtheid';
type DimensieScores = Record<Dimensie, number | null>;

export interface Kwaliteit {
  minimum_reviews: number;
  eigen: { aantal: number | null; score: number | null; dimensies: DimensieScores };
  eigen_onderdrukt: boolean;
  landelijk: { aantal: number; score: number | null; dimensies: DimensieScores };
  dimensies: { key: Dimensie; label: string; bron: string }[];
  opdrachtgevers: { employer_id: string; naam: string; score: number | null; aantal: number | null; onderdrukt: boolean }[];
}

export const haalKwaliteit = async (): Promise<Kwaliteit> => (await api.get(`${BASIS}/kwaliteit`)).data.data;

// ─── 5 Marktrapportage ───────────────────────────────────────────────────────

export type MarktRol = 'VTP' | 'BMW' | 'VITALITEIT';
export type Ervaring = '1-2' | '3-5' | '6-10' | '10+';

export interface Markt {
  rol: MarktRol;
  rol_label: string;
  jaar: number;
  kwartaal: number;
  periode: Periode;
  minimum_professionals: number;
  categorieen: { categorie: Ervaring; mediaan: number | null; professionals: number | null; onderdrukt: boolean }[];
  eigen_tarief: number | null;
  eigen_categorie: Ervaring | null;
}

export const haalMarkt = async (rol: MarktRol, jaar: number, kwartaal: number): Promise<Markt> =>
  (await api.get(`${BASIS}/markt`, { params: { rol, jaar, kwartaal } })).data.data;
